import { useTranslation } from 'react-i18next';
import { trDisplay } from '../../i18n';
/**
 * Unified Results Summary Components
 *
 * Provides summary statistics for simulation results.
 * Works for both Lombard (margin call risk) and SWR (depletion risk) strategies.
 */

import React from 'react';
import { Card, CardContent } from '../ui/card';
import {
  TrendingUp,
  AlertTriangle,
  Calendar,
  ShieldCheck,
  ShieldAlert,
  Clock,
  TrendingDown,
  Timer,
  HelpCircle,
  GraduationCap,
} from 'lucide-react';
import { formatNumber, formatPercentage } from '../../utils/formatters';
import { MobileTooltip } from '../ui/tooltip';
import { SWRHelpContent } from '../swr/SWRHelp';
import { StatCard } from '../common';

// ============================================
// CONFIGURATION BY STRATEGY TYPE
// ============================================

const LOMBARD_CONFIG = {
  theme: 'lombard',
  accentColor: 'bg-emerald-500',
  drawdownColor: 'bg-violet-500',
  title: 'Riepilogo risultati',
  successMetric: {
    title: 'Probabilità di non avere mai una margin call',
    field: 'noMarginCallProbability',
    subtitle: 'Scenari senza liquidazione forzata',
    icon: ShieldCheck,
    tooltip: 'Percentuale di simulazioni in cui non si verifica mai una margin call.',
    thresholds: { success: 0.7, warning: 0.5 },
  },
  riskMetric: {
    title: 'Probabilità di almeno una margin call',
    field: 'marginCallProbability',
    subtitle: 'Scenari con liquidazione forzata',
    icon: ShieldAlert,
    tooltip: 'Percentuale di simulazioni con almeno una margin call.',
    thresholds: { success: 0.3, warning: 0.5 },
  },
  yearMetric: {
    title: 'Anno mediano della prima margin call',
    field: 'medianMarginCallYear',
    subtitle: 'Solo percorsi con margin call',
    icon: Calendar,
    tooltip: 'Anno mediano della prima margin call tra le simulazioni che ne hanno una.',
  },
  rangeMetric: {
    title: 'Intervallo anni margin call',
    p10Field: 'p10MarginCallYear',
    p90Field: 'p90MarginCallYear',
    subtitle: '10° - 90° percentile',
    icon: Clock,
    tooltip: 'Intervallo dell\'80% centrale degli anni di margin call.',
  },
  statsLabels: {
    total: 'simulazioni totali',
    withEvent: 'con margin call',
  },
  drawdownExplanation: `In questa sezione il drawdown è calcolato solo sul valore del portafoglio investito,
    come se non effettuassi alcun prelievo.
    I prelievi e il debito Lombard vengono considerati nelle altre analisi
    (LTV, rischio di margin call, ecc.), ma qui guardiamo solo l'effetto dei
    rendimenti di mercato sul capitale.`,
};

const SWR_CONFIG = {
  theme: 'swr',
  accentColor: 'bg-blue-500',
  drawdownColor: 'bg-blue-400',
  title: 'Riepilogo risultati',
  successMetric: {
    title: 'Probabilità di non esaurire mai il capitale',
    field: 'noRuinProbability',
    subtitle: 'Per tutto l’orizzonte di simulazione',
    icon: ShieldCheck,
    tooltip: <SWRHelpContent topic="survival" />,
    thresholds: { success: 0.95, warning: 0.9 },
  },
  riskMetric: {
    title: 'Probabilità di esaurire il capitale',
    field: 'probabilityOfRuin',
    subtitle: "Almeno una volta entro l'orizzonte",
    icon: ShieldAlert,
    tooltip: <SWRHelpContent topic="depletion" />,
    thresholds: { success: 0.05, warning: 0.10 },
  },
  yearMetric: {
    title: 'Anno mediano di esaurimento',
    field: 'medianDepletionYear',
    subtitle: '50° percentile (solo percorsi esauriti)',
    icon: Calendar,
    tooltip: <SWRHelpContent topic="depletionYear" />,
  },
  rangeMetric: {
    title: 'Intervallo anni esaurimento',
    p10Field: 'p10DepletionYear',
    p90Field: 'p90DepletionYear',
    subtitle: '10° - 90° percentile',
    icon: Clock,
    tooltip: <SWRHelpContent topic="depletionRange" />,
  },
  statsLabels: {
    total: 'simulazioni totali',
    withEvent: 'con esaurimento',
  },
  drawdownExplanation: `Il drawdown di mercato isola i rendimenti e ipotizza nessun prelievo: con il buffer,
    la quota liquida segue il modello di rendimento cash selezionato e solo la quota investita segue il mercato.
    Il drawdown effettivo misura invece il patrimonio totale realmente disponibile,
    includendo prelievi, tasse, buffer e ricariche.`,
};

// ============================================
// UNIFIED RESULTS SUMMARY COMPONENT
// ============================================

/**
 * Unified Results Summary Component
 *
 * @param {Object} props
 * @param {Object} props.summary - Simulation summary object
 * @param {Object} props.drawdownStats - Drawdown statistics
 * @param {boolean} props.isEmpty - Show placeholder state
 * @param {string} props.variant - 'lombard' | 'swr'
 */
const UnifiedResultsSummary = ({ summary, drawdownStats, isEmpty = false, variant = 'lombard', detailMode = 'complete' }) => {
  useTranslation();
  const config = variant === 'swr' ? SWR_CONFIG : LOMBARD_CONFIG;
  const showPlaceholder = isEmpty || !summary;
  const showDetailedAnalysis = variant !== 'swr' || detailMode === 'complete';

  const displaySummary = showPlaceholder ? {} : summary;
  const marketDrawdownStats = variant === 'swr'
    ? drawdownStats?.market || drawdownStats
    : drawdownStats;
  const effectiveDrawdownStats = variant === 'swr' ? drawdownStats?.effective : null;

  // Determine variants for success/risk cards
  const successValue = displaySummary[config.successMetric.field];
  const riskValue = displaySummary[config.riskMetric.field];

  const getSuccessVariant = () => {
    if (showPlaceholder) return 'default';
    const { success, warning } = config.successMetric.thresholds;
    return successValue >= success ? 'success' : successValue >= warning ? 'warning' : 'danger';
  };

  const getRiskVariant = () => {
    if (showPlaceholder) return 'default';
    const { success, warning } = config.riskMetric.thresholds;
    return riskValue <= success ? 'success' : riskValue <= warning ? 'warning' : 'danger';
  };

  const formatValue = (value, formatter = (v) => v) => {
    if (showPlaceholder || value === null || value === undefined) return '–';
    return formatter(value);
  };

  const p10Year = displaySummary[config.rangeMetric.p10Field];
  const p90Year = displaySummary[config.rangeMetric.p90Field];
  const rangeValue = showPlaceholder ? '–' :
    (p10Year !== null && p90Year !== null)
      ? `${Math.round(p10Year)} - ${Math.round(p90Year)}`
      : '–';

  // Stats counts
  const totalField = variant === 'swr' ? 'totalSimulations' : 'totalSimulations';
  const withEventField = variant === 'swr' ? 'simulationsWithDepletion' : 'simulationsWithMarginCall';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-slate-800 flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${config.accentColor}`}></span>
          {trDisplay(config.title)}
        </h2>
        {trDisplay(variant === 'swr' && (
          <MobileTooltip
            content={<SWRHelpContent topic="summary" />}
            side="top"
          >
            <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
          </MobileTooltip>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          title={trDisplay(config.successMetric.title)}
          value={formatValue(successValue, formatPercentage)}
          subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : config.successMetric.subtitle)}
          icon={config.successMetric.icon}
          variant={getSuccessVariant()}
          tooltip={config.successMetric.tooltip}
        />

        <StatCard
          title={trDisplay(config.riskMetric.title)}
          value={formatValue(riskValue, formatPercentage)}
          subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : config.riskMetric.subtitle)}
          icon={config.riskMetric.icon}
          variant={getRiskVariant()}
          tooltip={config.riskMetric.tooltip}
        />

        <StatCard
          title={trDisplay(config.yearMetric.title)}
          value={formatValue(displaySummary[config.yearMetric.field], (v) => `Anno ${Math.round(v)}`)}
          subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : config.yearMetric.subtitle)}
          icon={config.yearMetric.icon}
          variant={showPlaceholder ? 'default' : 'warning'}
          tooltip={config.yearMetric.tooltip}
        />

        <StatCard
          title={trDisplay(config.rangeMetric.title)}
          value={rangeValue}
          subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : config.rangeMetric.subtitle)}
          icon={config.rangeMetric.icon}
          variant="default"
          tooltip={config.rangeMetric.tooltip}
        />
      </div>

      {/* Drawdown Analysis Section */}
      {trDisplay(showDetailedAnalysis && (showPlaceholder || drawdownStats) && (
        <div className="space-y-3 mt-4 sm:mt-6">
          <h3 className="text-sm sm:text-base font-semibold text-slate-700 flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${config.drawdownColor}`}></span>
            {trDisplay(variant === 'swr' ? 'Analisi del drawdown' : 'Analisi del drawdown del portafoglio')}
            <MobileTooltip
              content={variant === 'swr' ? <SWRHelpContent topic="drawdown" /> : (
                <div className="text-sm space-y-2 text-left">
                  <p className="font-medium">{trDisplay("Come viene calcolato il drawdown")}</p>
                  <p>{trDisplay(config.drawdownExplanation)}</p>
                </div>
              )}
              side="top"
            >
              <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
            </MobileTooltip>
          </h3>

          {trDisplay(variant === 'swr' && (
            <p className="text-xs font-semibold uppercase text-slate-500">{trDisplay("Drawdown di mercato senza prelievi")}</p>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title={trDisplay(variant === 'swr' ? 'Drawdown mercato mediano' : 'Drawdown massimo mediano')}
              value={formatValue(marketDrawdownStats?.medianDrawdownPct, formatPercentage)}
              subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : "Calo tipico dal picco")}
              icon={TrendingDown}
              variant="info"
              theme={config.theme}
              tooltip={variant === 'swr' ? <SWRHelpContent topic="marketDrawdown" /> : "Perdita massima tipica del portafoglio rispetto al suo picco storico (50° percentile)."}
            />

            <StatCard
              title={trDisplay(variant === 'swr' ? 'Drawdown mercato (90° perc.)' : 'Drawdown massimo (90° perc.)')}
              value={formatValue(marketDrawdownStats?.p90DrawdownPct, formatPercentage)}
              subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : "Scenario sfavorevole")}
              icon={TrendingDown}
              variant={showPlaceholder ? 'default' : (marketDrawdownStats?.p90DrawdownPct > 0.4 ? 'danger' : 'warning')}
              tooltip={variant === 'swr' ? <SWRHelpContent topic="marketDrawdownP90" /> : "Perdita massima nei casi più sfavorevoli: solo il 10% delle simulazioni ha un drawdown più profondo."}
            />

            <StatCard
              title={trDisplay("Durata drawdown mediana")}
              value={formatValue(marketDrawdownStats?.medianDrawdownDuration, (v) => `${formatNumber(v, { decimals: 1 })} anni`)}
              subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : "Tempo tipico sotto il picco")}
              icon={Timer}
              variant="info"
              theme={config.theme}
              tooltip={variant === 'swr' ? <SWRHelpContent topic="marketDuration" /> : "Numero tipico di anni consecutivi in cui il portafoglio resta sotto il massimo storico."}
            />

            <StatCard
              title={trDisplay("Durata drawdown (90° perc.)")}
              value={formatValue(marketDrawdownStats?.p90DrawdownDuration, (v) => `${formatNumber(v, { decimals: 1 })} anni`)}
              subtitle={trDisplay(showPlaceholder ? "Esegui simulazione" : "Scenario sfavorevole")}
              icon={Timer}
              variant={showPlaceholder ? 'default' : (marketDrawdownStats?.p90DrawdownDuration > 10 ? 'danger' : 'warning')}
              tooltip={variant === 'swr' ? <SWRHelpContent topic="marketDurationP90" /> : "Durata del periodo sotto il picco nei casi peggiori: solo il 10% delle simulazioni ha drawdown più lunghi."}
            />
          </div>

          {trDisplay(variant === 'swr' && (showPlaceholder || effectiveDrawdownStats) && (
            <>
              <p className="pt-2 text-xs font-semibold uppercase text-slate-500">{trDisplay("Drawdown effettivo del patrimonio totale")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <StatCard
                  title={trDisplay("Drawdown effettivo mediano")}
                  value={formatValue(effectiveDrawdownStats?.medianDrawdownPct, formatPercentage)}
                  subtitle={trDisplay(showPlaceholder ? 'Esegui simulazione' : 'Include prelievi, tasse e buffer')}
                  icon={TrendingDown}
                  variant="info"
                  theme={config.theme}
                  tooltip={<SWRHelpContent topic="effectiveDrawdown" />}
                />
                <StatCard
                  title={trDisplay("Drawdown effettivo (90° perc., tutti)")}
                  value={formatValue(effectiveDrawdownStats?.p90DrawdownPct, formatPercentage)}
                  subtitle={trDisplay(showPlaceholder
                    ? 'Esegui simulazione'
                    : effectiveDrawdownStats?.p90DrawdownPct >= 1
                      ? '100% quando il percentile include esaurimenti'
                      : 'Include percorsi sopravvissuti ed esauriti')}
                  icon={TrendingDown}
                  variant={showPlaceholder ? 'default' : (effectiveDrawdownStats?.p90DrawdownPct > 0.4 ? 'danger' : 'warning')}
                  tooltip={<SWRHelpContent topic="effectiveDrawdownP90" />}
                />
                <StatCard
                  title={trDisplay("Drawdown effettivo (90° perc., sopravvissuti)")}
                  value={formatValue(
                    effectiveDrawdownStats?.survivors?.p90DrawdownPct
                      ?? effectiveDrawdownStats?.p90DrawdownPctSurvivors,
                    formatPercentage
                  )}
                  subtitle={trDisplay(showPlaceholder
                    ? 'Esegui simulazione'
                    : `${formatNumber(
                      effectiveDrawdownStats?.survivors?.sampleCount
                        ?? effectiveDrawdownStats?.survivorSampleCount
                        ?? 0
                    )} percorsi non esauriti`)}
                  icon={ShieldCheck}
                  variant={showPlaceholder
                    ? 'default'
                    : (
                      (
                        effectiveDrawdownStats?.survivors?.p90DrawdownPct
                        ?? effectiveDrawdownStats?.p90DrawdownPctSurvivors
                        ?? 0
                      ) > 0.4
                        ? 'warning'
                        : 'info'
                    )}
                  theme={config.theme}
                  tooltip={<SWRHelpContent topic="survivorDrawdown" />}
                />
                <StatCard
                  title={trDisplay("Durata effettiva mediana")}
                  value={formatValue(effectiveDrawdownStats?.medianDrawdownDuration, (v) => `${formatNumber(v, { decimals: 1 })} anni`)}
                  subtitle={trDisplay(showPlaceholder ? 'Esegui simulazione' : 'Tempo tipico sotto il picco effettivo')}
                  icon={Timer}
                  variant="info"
                  theme={config.theme}
                  tooltip={<SWRHelpContent topic="effectiveDuration" />}
                />
                <StatCard
                  title={trDisplay("Durata effettiva (90° perc.)")}
                  value={formatValue(effectiveDrawdownStats?.p90DrawdownDuration, (v) => `${formatNumber(v, { decimals: 1 })} anni`)}
                  subtitle={trDisplay(showPlaceholder ? 'Esegui simulazione' : 'Scenario patrimoniale sfavorevole')}
                  icon={Timer}
                  variant={showPlaceholder ? 'default' : (effectiveDrawdownStats?.p90DrawdownDuration > 10 ? 'danger' : 'warning')}
                  tooltip={<SWRHelpContent topic="effectiveDurationP90" />}
                />
              </div>
            </>
          ))}

          {/* Threshold probability if configured (Lombard only) */}
          {trDisplay(!showPlaceholder && variant === 'lombard' && drawdownStats?.probExceedThreshold !== null && drawdownStats?.threshold > 0 && (
            <div className="mt-3">
              <Card className={`border shadow-sm ${drawdownStats.probExceedThreshold > 0.5 ? 'bg-red-50 border-red-200' : drawdownStats.probExceedThreshold > 0.25 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${drawdownStats.probExceedThreshold > 0.5 ? 'bg-red-100' : drawdownStats.probExceedThreshold > 0.25 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                      <Timer className={`h-5 w-5 ${drawdownStats.probExceedThreshold > 0.5 ? 'text-red-600' : drawdownStats.probExceedThreshold > 0.25 ? 'text-amber-600' : 'text-emerald-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">{trDisplay("Probabilità che il drawdown duri più di ")}<strong>{trDisplay(drawdownStats.threshold)}{trDisplay(" anni")}</strong>:
                      </p>
                      <p className={`text-xl font-bold ${drawdownStats.probExceedThreshold > 0.5 ? 'text-red-700' : drawdownStats.probExceedThreshold > 0.25 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {trDisplay(formatPercentage(drawdownStats.probExceedThreshold))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      ))}

      {trDisplay(!showPlaceholder && displaySummary[totalField] && (
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 pt-2">
          <span className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            {trDisplay(formatNumber(displaySummary[totalField]))} {trDisplay(config.statsLabels.total)}
          </span>
          <span className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            {trDisplay(formatNumber(displaySummary[withEventField]))} {trDisplay(config.statsLabels.withEvent)}
          </span>
          {trDisplay(displaySummary.seed != null && (
            <span className="text-slate-400">{trDisplay("Seed: ")}{trDisplay(displaySummary.seed)}
            </span>
          ))}
          {trDisplay(showDetailedAnalysis && variant === 'swr' && displaySummary.returnParameterUncertainty && (
            <MobileTooltip
              content={(
                <SWRHelpContent title={trDisplay("Ipotesi sui parametri di mercato")}
                  meaning={displaySummary.historicalPortfolio
                    ? 'I rendimenti e l’inflazione provengono dagli stessi blocchi storici di 12 mesi. Media e volatilità sono statistiche del portafoglio, senza una seconda estrazione parametrica.'
                    : displaySummary.returnParameterUncertainty.enabled
                    ? `Ogni percorso usa una media e una volatilità estratte da una distribuzione calibrata su ${displaySummary.returnParameterUncertainty.evidenceYears} anni ${displaySummary.returnParameterUncertainty.automatic ? 'di dati' : 'equivalenti'}. I parametri restano costanti nel percorso; i rendimenti mensili variano.`
                    : 'Media e volatilità Custom sono fisse tra i percorsi. I rendimenti mensili variano, ma non viene aggiunta l’incertezza nelle stime dei due parametri.'}
                  usage="Gli intervalli delle estrazioni descrivono l’incertezza aggiunta dal modello. Non sono intervalli dei rendimenti annui futuri e non coprono ogni possibile cambiamento dei mercati.">
                  {trDisplay(displaySummary.returnParameterUncertainty.enabled && (
                    <div className="space-y-1 text-xs">
                      <p>{trDisplay("Media estratta, 5°–95°: ")}<strong>{trDisplay(formatPercentage(displaySummary.returnParameterUncertainty.sampledAnnualExpectedReturnP05))} – {trDisplay(formatPercentage(displaySummary.returnParameterUncertainty.sampledAnnualExpectedReturnP95))}</strong></p>
                      <p>{trDisplay("Volatilità estratta, 5°–95°: ")}<strong>{trDisplay(formatPercentage(displaySummary.returnParameterUncertainty.sampledAnnualVolatilityP05))} – {trDisplay(formatPercentage(displaySummary.returnParameterUncertainty.sampledAnnualVolatilityP95))}</strong></p>
                    </div>
                  ))}
                </SWRHelpContent>
              )}
              side="top"
            >
              <span className={`flex cursor-help items-center gap-1 ${
                displaySummary.returnParameterUncertainty.enabled ? 'text-blue-600' : 'text-slate-500'
              }`}>
                <GraduationCap className="h-4 w-4" />
                {trDisplay(displaySummary.historicalPortfolio ? 'Bootstrap storico congiunto · blocchi di 12 mesi' : displaySummary.returnParameterUncertainty.enabled
                  ? `${displaySummary.returnParameterUncertainty.automatic ? 'Incertezza parametri automatica' : 'Incertezza parametri Custom attiva'} · ${displaySummary.returnParameterUncertainty.evidenceYears} anni ${displaySummary.returnParameterUncertainty.automatic ? 'di calibrazione' : 'equivalenti'}`
                  : 'Parametri Custom fissi · incertezza bayesiana disattivata')}
              </span>
            </MobileTooltip>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================
// BACKWARD COMPATIBILITY EXPORTS
// ============================================

/**
 * Lombard Results Summary (backward compatibility)
 */
export const ResultsSummary = (props) =>
  { useTranslation(); return <UnifiedResultsSummary {...props} variant="lombard" />; }
;

/**
 * SWR Results Summary (backward compatibility)
 */
export const SWRResultsSummary = (props) =>
  { useTranslation(); return <UnifiedResultsSummary {...props} variant="swr" />; }
;

export { UnifiedResultsSummary };
export default ResultsSummary;
